import React from 'react';
import { Info } from 'lucide-react';
import { MATCH_TYPE_CATEGORIES } from '../utils/matchTypeCatalog';

const EnemyStatsForm = ({
  formRef,
  onSubmit,
  onOpenConfig,
  state,
  actions,
  options,
  flags,
  onDeleteClick,
}) => {
  const {
    matchDate,
    teamRegion,
    teamName,
    region,
    enemyTeam,
    matchType,
    matchCategory,
    matchTypeRegion,
    mode,
    selectedMap,
    matchResult,
    pointsFor,
    pointsAgainst,
    mirrorEnabled,
    recordStatus,
    staleMirrorWarning,
    staleBaseWarning,
    orphanWarning,
  } = state;

  const {
    setMatchDate,
    setTeamRegion,
    setTeamName,
    setRegion,
    setEnemyTeam,
    setMatchType,
    setMatchCategory,
    setMatchTypeRegion,
    setMode,
    setSelectedMap,
    setMatchResult,
    setPointsFor,
    setPointsAgainst,
    setMirrorEnabled,
  } = actions;

  const { regions, vsTeams, matchTypes, matchTypeOptions: catalogMatchTypeOptions, matchTypeRegionOptions, mapOptions } = options;
  const { isSubmitting, teamId, isEdit } = flags;
  const normalize = (value) => String(value || '').trim();
  const uniqueList = (values) => {
    const seen = new Set();
    const out = [];
    values.forEach((value) => {
      const clean = normalize(value);
      if (!clean) return;
      const key = clean.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(clean);
    });
    return out;
  };

  const regionOptions = uniqueList([...(regions || []), teamRegion, region]);
  const teamOptions = uniqueList([
    ...(vsTeams || [])
      .filter((t) => normalize(t.region).toUpperCase() === normalize(teamRegion).toUpperCase())
      .map((t) => t.name),
    teamName
  ]);
  const enemyTeamOptions = uniqueList([
    ...(vsTeams || [])
      .filter((t) => normalize(t.region).toUpperCase() === normalize(region).toUpperCase())
      .map((t) => t.name),
    enemyTeam
  ]);
  const matchTypeOptions = uniqueList([...(catalogMatchTypeOptions || matchTypes || []), matchType]);
  const mapSelectOptions = uniqueList([...(mapOptions || []), selectedMap]);

  return (
    <form ref={formRef} onSubmit={onSubmit} className="add-stats-form team-summary-form enemy-stats-form">
      <header className="team-summary-header">
        <div className="team-summary-title-row">
          <div>
            <p className="team-summary-eyebrow">Opponent Scouting</p>
            <h2 className="section-header">Submit Opponent Stats</h2>
            <p className="team-summary-subtitle">
              Record an opponent match result for scouting and season tracking.
            </p>
          </div>

          {(recordStatus === 'LINKED' || recordStatus === 'ORPHAN') && (
            <div className={`team-summary-status enemy-record-status ${recordStatus === 'ORPHAN' ? 'is-orphan' : 'is-linked'}`}>
              {recordStatus === 'ORPHAN' ? 'Orphan - linked record missing; changes will not sync.' : 'Linked opponent record'}
            </div>
          )}
        </div>

      {(recordStatus === 'LINKED' || recordStatus === 'ORPHAN') && (
        <span className="sr-only">
          {recordStatus === 'ORPHAN' ? 'Orphan linked record missing; changes will not sync.' : 'Linked opponent record'}
        </span>
      )}
      {orphanWarning && (
        <div className="enemy-warning enemy-warning-orphan">
          {orphanWarning}
        </div>
      )}
      {(staleBaseWarning || staleMirrorWarning) && (
        <div className="enemy-warning enemy-warning-stale">
          {staleBaseWarning || staleMirrorWarning}
        </div>
      )}

        <div className="team-summary-notice">
          <Info size={14} aria-hidden="true" />
          <p>
            Match options are managed in{' '}
            <button type="button" onClick={onOpenConfig}>
              Database Configurations
            </button>
            .
          </p>
        </div>
      </header>

      <div className="summary-card-column enemy-left-column">
        <section className="form-card match-info-card">
          <h3>Match Info</h3>
          <div className="field-grid field-grid-two">
            <label className="summary-field">
              <span>Date</span>
              <input
                type="date"
                value={matchDate}
                onChange={e => setMatchDate(e.target.value)}
                required
              />
            </label>

            <label className="summary-field">
              <span>Team Region</span>
              <select required value={teamRegion} onChange={e => { setTeamRegion(e.target.value); setTeamName(''); }}>
                <option value="">Select Team Region</option>
                {regionOptions.map((reg, i) => <option key={i} value={reg}>{reg}</option>)}
              </select>
            </label>

            <label className="summary-field">
              <span>Team</span>
              <select required value={teamName} onChange={e => setTeamName(e.target.value)} disabled={!teamRegion}>
                <option value="">Select Team Name</option>
                {teamOptions.map((name, i) => <option key={i} value={name}>{name}</option>)}
              </select>
            </label>

            <label className="summary-field">
              <span>Opponent Region</span>
              <select required value={region} onChange={e => { setRegion(e.target.value); setEnemyTeam(''); }}>
                <option value="">Select Opponent Region</option>
                {regionOptions.map((reg, i) => <option key={i} value={reg}>{reg}</option>)}
              </select>
            </label>

            <label className="summary-field summary-field-wide">
              <span>Opponent Team</span>
              <select required value={enemyTeam} onChange={e => setEnemyTeam(e.target.value)} disabled={!region}>
                <option value="">Select Opponent Team</option>
                {enemyTeamOptions.map((name, i) => <option key={i} value={name}>{name}</option>)}
              </select>
            </label>

            <div className="summary-field-group-label">Match Classification</div>

            <label className="summary-field">
              <span>Match Category</span>
              <select
                required
                value={matchCategory}
                onChange={e => {
                  setMatchCategory(e.target.value);
                  setMatchType('');
                }}
              >
                {MATCH_TYPE_CATEGORIES.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="summary-field">
              <span>Competition Region</span>
              <select
                required
                value={matchTypeRegion}
                onChange={e => {
                  setMatchTypeRegion(e.target.value);
                  setMatchType('');
                }}
              >
                {(matchTypeRegionOptions || ['GLOBAL']).map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </label>

            <label className="summary-field summary-field-wide">
              <span>Competition</span>
              <select required value={matchType} onChange={e => setMatchType(e.target.value)}>
                <option value="">Select Competition</option>
                {matchTypeOptions.map((type, i) => <option key={i} value={type}>{type}</option>)}
              </select>
            </label>
          </div>
        </section>

        <section className="form-card points-card">
          <h3>Points</h3>
          <div className="field-grid field-grid-two">
            <label className="summary-field">
              <span>Points For</span>
              <input
                name="PF"
                type="number"
                placeholder="Points For"
                value={pointsFor}
                onChange={e => setPointsFor(e.target.value)}
              />
            </label>

            <label className="summary-field">
              <span>Points Against</span>
              <input
                name="PA"
                type="number"
                placeholder="Points Against"
                value={pointsAgainst}
                onChange={e => setPointsAgainst(e.target.value)}
              />
            </label>
          </div>
        </section>
      </div>

      <div className="summary-card-column enemy-right-column">
        <section className="form-card mode-map-card">
          <h3>Mode & Map</h3>
          <div className="field-grid field-grid-two">
            <label className="summary-field">
              <span>Mode</span>
              <select required value={mode} onChange={e => setMode(e.target.value)}>
                <option value="">Select Mode</option>
                <option value="HP">Hardpoint</option>
                <option value="SND">Search &amp; Destroy</option>
                <option value="CTL">Control</option>
              </select>
            </label>

            <label className="summary-field">
              <span>Map</span>
              <select required value={selectedMap} onChange={e => setSelectedMap(e.target.value)}>
                <option value="">Select Map</option>
                {mapSelectOptions.map((map) => <option key={map} value={map}>{map}</option>)}
              </select>
            </label>

            <label className="summary-field summary-field-wide">
              <span>Match Result</span>
              <select required value={matchResult} onChange={e => setMatchResult(e.target.value)}>
                <option value="">Match Result</option>
                <option value="Win">Win</option>
                <option value="Lose">Lose</option>
              </select>
            </label>
          </div>
        </section>

        <section className="form-card notes-card">
          <h3>Notes</h3>
          <label className="summary-field">
            <span>Match Notes</span>
            <textarea name="Notes" placeholder="Additional notes..." rows={3} />
          </label>
        </section>
      </div>

      <div className="team-summary-submit-row enemy-submit-row">
        {!isEdit && (
          <label className="enemy-mirror-option">
            <input
              type="checkbox"
              checked={mirrorEnabled}
              onChange={(e) => setMirrorEnabled(e.target.checked)}
            />
            <span className="enemy-mirror-copy">
              <span className="enemy-mirror-label-row">
                <span>Also save this match for the other team</span>
                <span
                  className="enemy-mirror-info"
                  tabIndex={0}
                  aria-label="Use this when manually adding an opponent match that isn't already in your database. Team Stats submissions automatically create the corresponding opponent record."
                >
                  i
                </span>
              </span>
              <span className="enemy-mirror-helper">
                Creates a mirrored record for both teams. Leave this off if this matchup was already submitted through Team Stats.
              </span>
            </span>
          </label>
        )}

        <div className="enemy-submit-actions">
          {isEdit && (
            <button
              type="button"
              onClick={onDeleteClick}
              disabled={isSubmitting}
              className="enemy-delete-button"
            >
              Delete
            </button>
          )}
          <button type="submit" disabled={!teamId || isSubmitting}>
            {isSubmitting ? 'Submitting...' : <>Submit Opponent Match <span aria-hidden="true">-&gt;</span></>}
          </button>
        </div>
      </div>
    </form>
  );
};

export default EnemyStatsForm;
